import {DndContext, DragEndEvent, DragStartEvent} from '@dnd-kit/core';
import {Show} from 'platform/foundation';

import {ReactNode} from 'react';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {BigCalendarDragOverlay} from './BigCalendarDragOverlay';

/**
 * Props for {@link BigCalendarDndContext}.
 */
export interface BigCalendarDndContextProps extends RequiredTestIdProps {
  /**
   * Sensors created via `useSensors(...)` that will be passed to the underlying
   * `DndContext`. The type is inferred directly from the `DndContext` props to
   * stay in sync with the library.
   */
  sensors: Parameters<typeof DndContext>[0]['sensors'];
  /**
   * When set to `true`, the component will **not render** the internal
   * `DndContext` and will simply return its children, effectively disabling any
   * drag-and-drop behaviour.
   */
  isReadOnly?: boolean;
  /**
   * Handler forwarded to the underlying `DndContext` that fires when a drag
   * operation starts.
   */
  onDragStart?: (event: DragStartEvent) => void;
  /**
   * Handler forwarded to the underlying `DndContext` that fires when a drag
   * operation ends.
   */
  onDragEnd?: (event: DragEndEvent) => void;
  /**
   * Elements that will participate in the drag-and-drop context.
   */
  children: ReactNode;
}

/**
 * A thin wrapper around dnd-kit's {@link DndContext} that disables the entire
 * drag-and-drop subsystem when `isReadOnly` is `true`. This is useful for
 * scenarios where the calendar should be displayed in a view-only mode without
 * registering any unnecessary drag listeners.
 *
 * @example
 * <BigCalendarDndContext
 *   isReadOnly={isReadOnly}
 *   sensors={sensors}
 *   onDragStart={handleDragStart}
 *   onDragEnd={handleDragEnd}
 * >
 *   <CalendarContent />
 * </BigCalendarDndContext>
 */
export function BigCalendarDndContext(props: BigCalendarDndContextProps) {
  if (props.isReadOnly) {
    return <>{props.children}</>;
  }

  return (
    <DndContext sensors={props.sensors} onDragStart={props.onDragStart} onDragEnd={props.onDragEnd}>
      {props.children}
      <Show when={!props.isReadOnly}>
        <BigCalendarDragOverlay data-testid={suffixTestId('dndContext', props)} />
      </Show>
    </DndContext>
  );
}
